// Onboarding configuration constants
import { getSuggestedStocksFromMotivations } from '../utils/motivationMapping';
import { discordInviteUrl } from '../constants';

// Popular stock symbols and names for the stock selection component
export const POPULAR_STOCKS = [
  { symbol: 'AAPL', name: 'Apple' },
  { symbol: 'MSFT', name: 'Microsoft' },
  { symbol: 'GOOGL', name: 'Alphabet' },
  { symbol: 'AMZN', name: 'Amazon' },
  { symbol: 'TSLA', name: 'Tesla' },
  { symbol: 'NVDA', name: 'NVIDIA' },
  { symbol: 'META', name: 'Meta' },
  { symbol: 'NFLX', name: 'Netflix' },
  { symbol: 'UBER', name: 'Uber' },
  { symbol: 'SPOT', name: 'Spotify' },
  { symbol: 'BRKB', name: 'Berkshire Hathaway' },
  { symbol: 'CRM', name: 'Salesforce' },
  { symbol: 'PLTR', name: 'Palantir' },
  { symbol: 'ZM', name: 'Zoom' },
  { symbol: 'SPY', name: 'S&P 500 ETF' },
  { symbol: 'QQQ', name: 'Nasdaq ETF' },
  { symbol: 'VTI', name: 'Total Market ETF' },
  { symbol: 'COST', name: 'Costco' },
  { symbol: 'V', name: 'Visa' },
  { symbol: 'AVGO', name: 'Broadcom' },
  { symbol: 'KO', name: 'Coca-Cola' },
  { symbol: 'JPM', name: 'JPMorgan Chase' },
  { symbol: 'JNJ', name: 'Johnson & Johnson' },
  { symbol: 'WMT', name: 'Walmart' },
] as const;

// Get personalized stock suggestions based on user motivations
export const getPersonalizedStockSuggestions = (
  motivations: string[] = [],
): Array<{ symbol: string; name: string }> => {
  try {
    // Input validation
    if (!Array.isArray(motivations)) {
      console.warn('getPersonalizedStockSuggestions: motivations must be an array, falling back to default');
      motivations = [];
    }

    const suggestedSymbols = getSuggestedStocksFromMotivations(motivations);

    // Ensure we got a valid array
    if (!Array.isArray(suggestedSymbols) || suggestedSymbols.length === 0) {
      console.warn('getSuggestedStocksFromMotivations returned invalid data, using fallback');
      return POPULAR_STOCKS.slice(0, 6); // Safe fallback to first 6 popular stocks
    }

    return suggestedSymbols.map((symbol) => {
      try {
        const stock = POPULAR_STOCKS.find((s) => s.symbol === symbol);
        return stock || { symbol, name: `${symbol} Inc.` };
      } catch (error) {
        console.warn(`Error processing symbol ${symbol}:`, error);
        return { symbol: symbol || 'AAPL', name: `${symbol || 'Apple'} Inc.` };
      }
    });
  } catch (error) {
    console.error('Error in getPersonalizedStockSuggestions:', error);
    // Return safe fallback
    return POPULAR_STOCKS.slice(0, 6);
  }
};

// Default stock symbols when user selects "no holdings"
export const DEFAULT_NO_HOLDINGS_SYMBOLS = ['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'TSLA'] as const;

// Default suggested questions for different chat contexts
export const DEFAULT_SUGGESTED_QUESTIONS = {
  portfolioInsights: [
    'How is AAPL positioned for AI relative to GOOGL?',
    "What's the latest news on Netflix? Is this bullish for Spotify?",
    'What stocks could be undervalued?',
    'What are some growth opportunities?',
    'How should I diversify my portfolio?',
    'Where is the real money being made right now?',
    'What am I leaving on the table in my portfolio?',
    'What are the best dividend stocks right now?',
  ],
  investmentStrategies: [
    'What are some defensive investment strategies?',
    'How should I rebalance my portfolio?',
    'What sectors are undervalued right now?',
    'How do I play defense without losing my edge?',
    'When should I press harder and when should I cut?',
    'What sectors have momentum right now?',
  ],
} as const;

// Layout and sizing constants
export const ONBOARDING_LAYOUT = {
  // Image dimensions
  researchImageWidth: '280px',
  researchImageHeight: '250px',

  // Container dimensions
  maxContentWidth: '500px',

  // Spacing values (in addition to theme spacing)
  titlePadding: '24px', // spacing.regular
  contentPadding: '16px', // spacing.deci

  // Button dimensions
  stockButtonMinWidth: '120px',
  symbolPillBorderRadius: '20px',
  removeButtonSize: '16px',

  // Animation durations
  transitionDuration: '0.2s',
  hoverTransitionDuration: '0.3s',
} as const;

// Chat configuration
export const CHAT_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // ms
  typingDelay: 50, // ms per character for typing effect
  maxMessageLength: 5000,
  suggestionLoadTimeout: 5000, // ms
} as const;

// Auto-message prompts (now integrated with motivation mapping)
export const AUTO_CHAT_PROMPTS = {
  noHoldings:
    "I don't have any holdings right now, please analyze some popular stocks and give me some unique insights.",
  portfolioInsights: (symbols: string[]) =>
    `This is my portfolio: ${symbols.join(', ')}. Give me the top 3 insights about what I could be missing.`,
  investmentStrategies: (symbols: string[]) =>
    `Based on my current holdings (${symbols.join(', ')}), what investment strategies could help me diversify and potentially outperform the market?`,
  weeklyUpdate: (symbols: string[]) =>
    `Give me a brief weekly market update focused on ${symbols.join(', ')} and any major market developments I should be aware of.`,
  /** Context-aware AI portfolio question (used when user's top stock comes from an AI portfolio or curated picks) */
  aiPortfolioQuestion: (ticker: string) => `What's the bull case and bear case for ${ticker}?`,
} as const;

// Validation rules
export const VALIDATION = {
  minSelectedStocks: 1,
  maxSelectedStocks: 10,
  symbolPattern: /^[A-Z]{1,5}$/,
  timeoutDurations: {
    navigation: 100, // ms
    apiCall: 10000, // ms
    animation: 300, // ms
  },
} as const;

// Community links for onboarding result page
export const COMMUNITY_LINKS = {
  whatsapp: 'https://chat.whatsapp.com/F0m0yPWeh4XFu8iAv6Vmrx',
  discord: discordInviteUrl,
} as const;
